// Cada rango de hologramas de un lote se puede repartir en varias salidas (sub-rangos con tipo de salida opcional).
// El servidor (BackendInspeccion) guarda el estatus de cada rango y de cada salida.

export const OPCIONES_TIPO_SALIDA = [
  "Venta Nacional",
  "Venta Exportación",
  "Salida a otro almacén de producto terminado",
  "Baja o Merma"
];

const esMerma = (tipo) => /merma|^baja/i.test(String(tipo || ""));
const num = (v) => parseInt(String(v ?? "").replace(/\D/g, ""), 10);

export const cantidadRango = (r) => {
  const c = parseInt(r?.cantidad || r?.cantidadInicial || 0, 10);
  return isNaN(c) || c < 0 ? 0 : c;
};

// Rangos con folios y cantidad capturados
export const rangosValidos = (rangos = []) =>
  rangos.filter(r =>
    cantidadRango(r) > 0 &&
    String(r.rango1 || r.rango_inicial || "").trim() !== "" &&
    String(r.rango2 || r.rango_final || "").trim() !== "" &&
    !String(r.rangoHologramas || "").includes("nválido")
  );

export const totalRangos = (rangos = []) =>
  rangosValidos(rangos).reduce((acc, r) => acc + cantidadRango(r), 0);

// Salidas de un rango con folios y cantidad capturados (las filas vacías no se envían)
export const salidasValidas = (rango) =>
  (rango?.salidas || []).filter(x =>
    (parseInt(x.cantidad, 10) || 0) > 0 &&
    String(x.rango1 ?? x.rango_inicial ?? "").trim() !== "" &&
    String(x.rango2 ?? x.rango_final ?? "").trim() !== ""
  );

// Rangos listos para enviar al servidor: solo las salidas completas (el resto se descarta)
export const rangosParaEnviar = (rangos = []) =>
  rangos.map(r => (Array.isArray(r.salidas) ? { ...r, salidas: salidasValidas(r) } : r));

// Valida las salidas de un rango: dentro del rango, folios = cantidad y sin traslapes. Devuelve mensaje o null.
export const validarSalidasDeRango = (rango) => {
  const lista = salidasValidas(rango);
  if (lista.length === 0) return null;
  const nombre = rango.rangoHologramas || `${rango.prefijo || "A"}${rango.rango1} - ${rango.prefijo || "A"}${rango.rango2}`;
  const rIni = num(rango.rango1 ?? rango.rango_inicial);
  const rFin = num(rango.rango2 ?? rango.rango_final);
  const pref = rango.prefijo || "A";
  const filas = [];
  for (const x of lista) {
    const a = num(x.rango1 ?? x.rango_inicial);
    const b = num(x.rango2 ?? x.rango_final);
    const cant = parseInt(x.cantidad, 10) || 0;
    const txt = `${pref}${x.rango1 ?? x.rango_inicial} - ${pref}${x.rango2 ?? x.rango_final}`;
    if (isNaN(a) || isNaN(b) || b < a) return `La salida ${txt} del rango ${nombre} tiene folios inválidos.`;
    if (a < rIni || b > rFin) return `La salida ${txt} debe estar dentro del rango ${nombre}.`;
    if (/almac[eé]n/i.test(String(x.tipo_salida || "")) && !String(x.almacen_destino || "").trim()) {
      return `La salida ${txt} del rango ${nombre} requiere seleccionar el almacén de destino.`;
    }
    filas.push({ a, b, txt, vigente: String(x.estatus || "").toLowerCase() !== "rechazado" });
  }
  const vigentes = filas.filter(f => f.vigente);
  for (let i = 0; i < vigentes.length; i++) {
    for (let j = i + 1; j < vigentes.length; j++) {
      if (vigentes[i].a <= vigentes[j].b && vigentes[j].a <= vigentes[i].b) {
        return `Las salidas ${vigentes[i].txt} y ${vigentes[j].txt} del rango ${nombre} se traslapan.`;
      }
    }
  }
  return null;
};

// Rangos que ya no cuentan como salida (rechazados por la UI)
const cuentaComoSalida = (r) => String(r?.estatus || "").toLowerCase() !== "rechazado";

/**
 * Valida los rangos nuevos contra el número de botellas del lote.
 * `yaAsignados` son los rangos ya guardados del lote (cuentan para el tope y los traslapes).
 * Devuelve un mensaje de error o null.
 */
export const validarRangosSalida = (nuevos = [], botellasLote = 0, yaAsignados = []) => {
  const validos = rangosValidos(nuevos);
  for (const r of validos) {
    const errorSalidas = validarSalidasDeRango(r);
    if (errorSalidas) return errorSalidas;
  }

  const previos = (yaAsignados || []).filter(cuentaComoSalida);
  const asignado = totalRangos(previos) + validos.reduce((acc, r) => acc + cantidadRango(r), 0);
  if (botellasLote > 0 && asignado > botellasLote) {
    return `Los rangos suman ${asignado} hologramas y el lote solo tiene ${botellasLote} botellas.`;
  }

  const todos = [...previos, ...validos];
  const bounds = (r) => ({
    p: String(r.prefijo || "A").trim().toUpperCase(),
    a: num(r.rango1 ?? r.rango_inicial),
    b: num(r.rango2 ?? r.rango_final)
  });
  const nuevosIdx = new Set(validos);
  for (let i = 0; i < todos.length; i++) {
    for (let j = i + 1; j < todos.length; j++) {
      if (!nuevosIdx.has(todos[i]) && !nuevosIdx.has(todos[j])) continue;
      const x = bounds(todos[i]);
      const y = bounds(todos[j]);
      if (x.p === y.p && ![x.a, x.b, y.a, y.b].some(isNaN) && x.a <= y.b && y.a <= x.b) {
        return `Los rangos ${x.p}${x.a} - ${x.p}${x.b} y ${y.p}${y.a} - ${y.p}${y.b} se traslapan.`;
      }
    }
  }
  return null;
};

/**
 * Totales del lote derivados de sus rangos (salidas):
 * salida = hologramas asignados, inventario final = botellas del lote - salida.
 */
export const resumenSalidasDesdeRangos = (rangos = [], { entradaBot = 0, entradaCaj = 0, botPorCaja = 12 } = {}) => {
  const lista = rangosValidos(rangos).filter(cuentaComoSalida);
  const porCaja = parseInt(String(botPorCaja).replace(/\D/g, ""), 10) || 12;

  // Extraer las salidas reales (sub-rangos) definidas en cada rango de hologramas
  const todasSalidas = lista.flatMap(rg => {
    const subs = salidasValidas(rg).filter(cuentaComoSalida);
    if (subs.length > 0) return subs;
    // Soporte para registros legados que guardaron tipo de salida directo en el rango
    if (rg.tipo_salida || rg.tipoSalida) {
      return [{ cantidad: cantidadRango(rg), tipo_salida: rg.tipo_salida || rg.tipoSalida }];
    }
    return [];
  });

  // Salidas reales como producto terminado: únicamente las botellas que se dieron de salida
  const salidaBot = todasSalidas.reduce((acc, x) => acc + (parseInt(x.cantidad, 10) || 0), 0);
  const mermas = todasSalidas.filter(x => esMerma(x.tipo_salida || x.tipoSalida)).reduce((acc, x) => acc + (parseInt(x.cantidad, 10) || 0), 0);
  const salidaCaj = Math.floor(salidaBot / porCaja);
  const tipos = [...new Set(todasSalidas.map(x => x.tipo_salida || x.tipoSalida).filter(Boolean))];
  const totalHologramas = lista.reduce((acc, r) => acc + cantidadRango(r), 0);

  return {
    salidaBotellas: salidaBot,
    salidaCajas: salidaCaj,
    salidaDestino: tipos.join(", "),
    invFinalBotellas: Math.max(0, entradaBot - salidaBot),
    invFinalCajas: Math.max(0, Math.floor((entradaBot - salidaBot) / porCaja)),
    mermas,
    totalHologramas
  };
};

// Los rangos de un lote visibles como "salidas" (cada uno con sus salidas internas)
export const salidasDeLote = (rec) => {
  const rangos = rec?.rangosHologramas || rec?.rangos_hologramas || [];
  return rangos.map(r => {
    const pref = r.prefijo || "A";
    const ini = r.rango1 || r.rango_inicial || "";
    const fin = r.rango2 || r.rango_final || "";
    return {
      id: r.id_producto_holograma || r.id,
      tipoSalida: r.tipo_salida || r.tipoSalida || "",
      botellas: cantidadRango(r),
      prefijo: pref,
      folioInicial: ini,
      folioFinal: fin,
      rangoHologramas: r.rangoHologramas || r.rango_completo || `${pref}${ini} - ${pref}${fin}`,
      estatus: String(r.estatus || "pendiente").toLowerCase(),
      observaciones: r.observaciones || "",
      observacionesCorreccion: r.observaciones_correccion || "",
      salidas: (r.salidas || []).map(x => ({
        ...x,
        id_salida: x.id_salida || x.id,
        tipo_salida: x.tipo_salida || x.tipoSalida || "",
        estatus: String(x.estatus || "pendiente").toLowerCase()
      }))
    };
  });
};

// Saldo de botellas del lote: entrada - rangos asignados (sin contar los rechazados)
export const saldoBotellasLote = (rec) => {
  const entrada = parseInt(rec?.entradaBotellas ?? rec?.entrada_botellas ?? rec?.invInicialBotellas ?? 0, 10) || 0;
  const asignado = salidasDeLote(rec)
    .filter(s => s.estatus !== "rechazado")
    .reduce((acc, s) => acc + s.botellas, 0);
  return { entrada, asignado, saldo: Math.max(0, entrada - asignado) };
};
